import type { JSX } from 'react';
import { createPortal } from 'react-dom';
import { appendDecimal, appendDigit, backspace } from './keypad-state';

export type KeypadProps = {
  value: string;
  onChange: (next: string) => void;
  onClose: () => void;
  decimal?: boolean;
};

export function Keypad({
  value,
  onChange,
  onClose,
  decimal = true,
}: KeypadProps): JSX.Element | null {
  const press = (digit: string) => () => onChange(appendDigit(value, digit));
  const onDot = () => onChange(appendDecimal(value));
  const onBackspace = () => onChange(backspace(value));

  if (typeof document === 'undefined') return null;

  // Portal to body so ancestors with `backdrop-filter` / `transform` / `filter`
  // don't become the containing block for our `position: fixed` overlay (CSS
  // spec: those properties create a containing block for fixed descendants).
  // Without this, NumericInput inside a Card (`backdrop-blur-sm`) would render
  // the keypad inline within the card instead of pinned to the viewport.
  return createPortal(
    <div
      role="dialog"
      aria-label="Numeric keypad"
      className="fixed inset-x-0 bottom-0 z-50 grid grid-cols-3 gap-2 border-t border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
      style={{
        paddingTop: '0.75rem',
        paddingLeft: 'calc(env(safe-area-inset-left, 0px) + 0.75rem)',
        paddingRight: 'calc(env(safe-area-inset-right, 0px) + 0.75rem)',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)',
      }}
    >
      {(['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const).map((d) => (
        <button
          key={d}
          type="button"
          onClick={press(d)}
          className="h-14 rounded-xl bg-slate-100 text-2xl font-medium hover:bg-slate-200 active:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700"
          aria-label={`Digit ${d}`}
        >
          {d}
        </button>
      ))}
      <button
        type="button"
        onClick={onDot}
        disabled={!decimal}
        className="h-14 rounded-xl bg-slate-100 text-2xl font-medium hover:bg-slate-200 disabled:opacity-30 dark:bg-slate-800 dark:hover:bg-slate-700"
        aria-label="Decimal point"
      >
        .
      </button>
      <button
        type="button"
        onClick={press('0')}
        className="h-14 rounded-xl bg-slate-100 text-2xl font-medium hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700"
        aria-label="Digit 0"
      >
        0
      </button>
      <button
        type="button"
        onClick={onBackspace}
        className="h-14 rounded-xl bg-slate-100 text-xl font-medium hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700"
        aria-label="Backspace"
      >
        ⌫
      </button>
      <button
        type="button"
        onClick={onClose}
        className="col-span-3 h-12 rounded-xl bg-slate-900 text-base font-medium text-white hover:bg-slate-800 dark:bg-slate-50 dark:text-slate-900"
        aria-label="Close keypad"
      >
        Done
      </button>
    </div>,
    document.body,
  );
}
